import React, { useState } from 'react';
import { Button, Form, FormGroup } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { tzDiff, tzNormalizeDate } from '../../../../utils/date';
import { getLastBusRetentionScheduleByRegisteredBus } from '../../../../actions';
import Loader from '../../../common/Loader';
import FormItem from '../../../common/forms/FormItem';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import { isRequired } from '../../../../utils/validators';
import LocationSelect from '../../../common/forms/select/LocationSelect';
import FormFooter from '../../../common/forms/FormFooter';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import TextInput from '../../../common/forms/input/TextInput';

const BusRetentionScheduleForm = ({
  handleSubmit,
  onCancel,
  dispatchGetLastBusRetentionScheduleByRegisteredBus,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastRetention, setLastRetention] = useState(null);
  const [isFetched, setIsFetched] = useState(false);
  const [daysDiff, setDaysDiff] = useState(0);

  const onRegisteredBusChange = async (value) => {
    if (!value) {
      setLastRetention(null);
      setIsFetched(false);
      setDaysDiff(0);
      return;
    }

    const { id } = value;
    setIsLoading(true);

    const response = await dispatchGetLastBusRetentionScheduleByRegisteredBus(
      id,
    );

    if (response !== null) {
      setLastRetention(response);
      try {
        const daysDifference = tzDiff({
          startDate: new Date(),
          endDate: response.startDate,
          unit: 'days',
        });
        setDaysDiff(daysDifference);
      } catch (e) {
        setDaysDiff(0);
      }
    } else {
      setLastRetention(null);
      setDaysDiff(0);
    }

    setIsLoading(false);
    setIsFetched(true);
  };

  const messageDaysDiff = (diff) => {
    if (diff > 0) {
      return `en ${diff} ${diff === 1 ? 'día' : 'días'}`;
    } else if (diff < 0) {
      return `hace ${-diff} ${Math.abs(diff) === 1 ? 'día' : 'días'}`;
    } else if (diff === 0) {
      return 'hoy';
    }
    return '';
  };

  const messageRangeRetention = (startDate, endDate) =>
    `Desde ${tzNormalizeDate({
      date: startDate,
      format: DATE_TIME_FORMAT,
    })} - Hasta ${tzNormalizeDate({
      date: endDate,
      format: DATE_TIME_FORMAT,
    })}`;

  const lastRetentionInfo = lastRetention ? (
    <>
      {daysDiff < 0 ? (
        <small className="form-text text-muted">
          Su último retén fue {messageDaysDiff(daysDiff)}:{' '}
          {messageRangeRetention(
            lastRetention.startDate,
            lastRetention.endDate,
          )}
        </small>
      ) : (
        <small className="form-text text-muted">
          Su último retén inicia {messageDaysDiff(daysDiff)}:{' '}
          {messageRangeRetention(
            lastRetention.startDate,
            lastRetention.endDate,
          )}
        </small>
      )}

      {lastRetention.itineraryId ? (
        <small className="form-text text-muted">
          Retén Activado ID: {lastRetention.itineraryId}
        </small>
      ) : null}
    </>
  ) : (
    <small className="form-text text-muted">
      No hay una retención previa para este bus.
    </small>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row>
        <FormItem label="Buses Asignados" required>
          <Field
            name="registeredBus"
            component={RegisteredBusSelect}
            validate={[isRequired]}
            onlyActive
            onChange={onRegisteredBusChange}
          />
          {isFetched && lastRetentionInfo}
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Desde" required>
          <Field
            name="fromDateString"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Hasta" required>
          <Field
            name="toDateString"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Ubicación" required>
          <Field
            name="location"
            component={LocationSelect}
            validate={[isRequired]}
            clearOnUnmount={false}
          />
        </FormItem>
      </FormGroup>
      <FormFooter>
        <Button
          type="button"
          color="secondary"
          className="mr-2"
          onClick={onCancel}
        >
          Cancelar
        </Button>
      </FormFooter>
    </Form>
  );
};

BusRetentionScheduleForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  dispatchGetLastBusRetentionScheduleByRegisteredBus: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  dispatchGetLastBusRetentionScheduleByRegisteredBus:
    getLastBusRetentionScheduleByRegisteredBus,
};

const form = reduxForm({
  form: 'busRetentionScheduleForm',
})(BusRetentionScheduleForm);

export default connect(null, mapDispatchToProps)(form);
